import { Component } from 'react';

import { RootState } from '@iwProvider/configureStore';
import classNames from 'classnames';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import AddPhoneNumberForm from './addPhoneNumberForm/AddPhoneNumberForm';
import styles from './PeopleToVerify.module.css';
import SelectPhoneNumberForm from './selectPhoneNumberForm/SelectPhoneNumberForm';
import { FormattedName } from '../../../../iwIntlProvider/FormattedName';
import { ErrorStatusSVG } from '../../../../svgComponents/errorStatusSVG/ErrorStatusSVG';
import { GreenTickStatusSVG } from '../../../../svgComponents/greenTickStatusSVG/GreenTickStatusSVG';
import { NotStartedStatusSVG } from '../../../../svgComponents/notStartedStatusSVG/NotStartedStatusSVG';
import { PendingStatusSVG } from '../../../../svgComponents/pendingStatusSVG/PendingStatusSVG';
import { TDeserialisedPerson } from '../IdCheckVerificationModal.types';

const mapStateToProps = (state: RootState) => state.idCheckVerificationModal;

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

class PeopleToVerify extends Component<PropsFromRedux> {
    renderForm(person: TDeserialisedPerson) {
        if (person.addPhoneToggle) {
            return <AddPhoneNumberForm person={person} />;
        }

        return <SelectPhoneNumberForm person={person} />;
    }

    renderPeople() {
        const { peopleToVerify } = this.props;

        return _.map(peopleToVerify, (person, index) => {
            const hasSMSFailed = person.status === 'SMS failed';
            const reachedMaxIdCheckAttempts = person.reachedMaxIdCheckAttempts;
            const name =
                peopleToVerify.length === 1 ? null : (
                    <FormattedName
                        firstName={person.firstName}
                        possessive={true}
                    />
                );

            return (
                <div className={styles.PersonToVerify} key={index}>
                    <div className={styles.personContainer}>
                        <div
                            className={classNames(
                                styles.nameSection,
                                'fs-exclude'
                            )}
                        >
                            {person.firstName} {person.lastName}
                        </div>
                        {!person.addPhoneToggle ? (
                            <span className={styles.buttonStatus}>
                                {this.renderButtonStatus(
                                    person.idCheckStatus,
                                    hasSMSFailed
                                )}
                            </span>
                        ) : null}
                        <div className={styles.formWrapper}>
                            {this.renderForm(person)}
                            {!person.addPhoneToggle ? (
                                <div
                                    className={styles.status}
                                    data-testid="status-message"
                                >
                                    {this.renderStatusMessage(
                                        person.idCheckStatus,
                                        name!,
                                        hasSMSFailed,
                                        reachedMaxIdCheckAttempts!
                                    )}
                                    <span className={styles.icon}>
                                        {this.renderIcon(
                                            person.idCheckStatus,
                                            hasSMSFailed,
                                            reachedMaxIdCheckAttempts!
                                        )}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            );
        });
    }

    render() {
        const people = this.renderPeople();

        return <div>{people}</div>;
    }

    renderStatusMessage(
        status: TDeserialisedPerson['idCheckStatus'],
        firstName: React.ReactNode,
        smsFailed: boolean,
        reachedMaxIdCheckAttempts: boolean
    ) {
        if (
            smsFailed ||
            status === 'Something went wrong' ||
            reachedMaxIdCheckAttempts
        ) {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.something_went_wrong"
                    defaultMessage="Something went wrong - please send another SMS and try again"
                />
            );
        }
        if (status === 'Not started yet') {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.not_started_yet"
                    defaultMessage="Not started yet"
                />
            );
        }
        if (status === 'Verified') {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.verified"
                    defaultMessage="Verified"
                />
            );
        }
        if (firstName) {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.verifying_name_check"
                    defaultMessage="Verifying {values} check"
                    values={{ values: firstName }}
                />
            );
        } else {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.verifying_check"
                    defaultMessage="Verifying your check"
                />
            );
        }
    }

    renderButtonStatus(
        status: TDeserialisedPerson['idCheckStatus'],
        smsFailed: boolean
    ) {
        if (status.includes('Verifying')) {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.did_not_receive_text"
                    defaultMessage="Didn't receive it?"
                />
            );
        } else if (status === 'Something went wrong' || smsFailed) {
            return (
                <FormattedMessage
                    id="_.modal.id_check_verification.lets_try_that_again"
                    defaultMessage="Let's try that again!"
                />
            );
        }
    }

    renderIcon(
        status: TDeserialisedPerson['idCheckStatus'],
        smsFailed: boolean,
        reachedMaxIdCheckAttempts: boolean
    ) {
        if (
            status === 'Something went wrong' ||
            smsFailed ||
            reachedMaxIdCheckAttempts
        ) {
            return <ErrorStatusSVG />;
        }

        switch (status) {
            case 'Not started yet':
                return <NotStartedStatusSVG />;
            case 'Verified':
                return <GreenTickStatusSVG />;

            default:
                return <PendingStatusSVG />;
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(PeopleToVerify);
